import { useEffect, useRef } from 'react'
import { useGetSocket, useSocketActions } from 'src/store/socket'
import { useGetCurrentPlayerToken } from 'src/store/player'

/**
 * Dynamically determines the WebSocket URL based on the current hostname.
 * This allows the same deployment to serve multiple domains (e.g., for geo-blocked regions).
 *
 * Examples:
 * - game.stagingmarbles.io → ws-staging.stagingmarbles.io
 * - game.stagingmarbles2.io → ws-staging.stagingmarbles2.io
 * - game.marbles.xyz → ws-production.marbles.xyz
 * - game.marbles-game.com → ws-production.marbles-game.com
 * - localhost → falls back to env var
 * - preview-xxx.vercel.app → falls back to env var
 */
const getSocketUrl = (): string => {
  const fallback = process.env.NEXT_PUBLIC_PHOENIX_SOCKET_URL || ''

  // Server-side rendering - use env var
  if (typeof window === 'undefined') {
    return fallback
  }

  const hostname = window.location.hostname

  // Local development - use env var
  if (hostname === 'localhost' || hostname.startsWith('127.') || hostname.startsWith('192.168.')) {
    return fallback
  }

  // Vercel preview deployments - use env var
  if (hostname.endsWith('.vercel.app')) {
    return fallback
  }

  // IP address check - use env var
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return fallback
  }

  // Extract base domain: game.stagingmarbles2.io → stagingmarbles2.io
  const parts = hostname.split('.')
  if (parts.length < 2) {
    return fallback
  }

  const baseDomain = parts.slice(1).join('.')

  // Determine WebSocket prefix based on environment
  let wsPrefix = 'ws-production'
  if (hostname.includes('development')) {
    wsPrefix = 'ws-development'
  } else if (hostname.includes('staging')) {
    wsPrefix = 'ws-staging'
  }

  return `${wsPrefix}.${baseDomain}`
}

export const useSocketManager = () => {
  const token = useGetCurrentPlayerToken()
  const socket = useGetSocket()
  const socketActions = useSocketActions()
  const socketCreated = useRef(false)

  useEffect(() => {
    if (!token || socketCreated.current) return

    socketActions.createSocket(process.env.NEXT_PUBLIC_PHOENIX_SOCKET_SCHEME, getSocketUrl(), token)
    socketCreated.current = true
  }, [token, socketActions])

  return {
    isSocketReady: Boolean(socket)
  }
}
